/**
 * Loop Roll-Up Report Page
 * 
 * Aggregate view of all sessions within a Catchball loop.
 * This is the reconciliation layer - showing how synthesis emerged from sessions.
 * 
 * Purpose: "Across all sessions in this loop, what picture emerged — and how did it evolve?"
 * 
 * Design Invariant: Loop reports aggregate session results; they do not override them.
 * Sessions remain the source of atomic truth.
 */

import { ObjectRef } from '@/components/ds/ObjectRef';
import { useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  GitBranch,
  Clock,
  Target,
  ListOrdered,
  CheckCircle,
  AlertTriangle,
  Info,
  Lightbulb,
  TrendingUp,
  Layers,
  ExternalLink,
} from 'lucide-react';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import {
  LOOP_STATE_LABELS,
  getBallPossession,
  getLoopProgress,
  type LoopSynthesis,
} from '../../../types/prioritization/catchballLoop';
import type { PrioritizationSession } from '../../../types/prioritization/session';

// ============================================================================
// Types
// ============================================================================

interface SessionEvolution {
  sessionId: string;
  sessionName: string;
  completedAt?: Date;
  itemCount: number;
  hasOutput: boolean;
  rankings: { itemId: string; rank: number; score: number }[];
}

// ============================================================================
// Component
// ============================================================================

export function LoopRollUpReport() {
  const { loopId } = useParams<{ loopId: string }>();
  
  // Get loop
  const loops = useCatchballLoopStore(state => state.loops);
  const loop = useMemo(
    () => loops.find(l => l.id === loopId),
    [loops, loopId]
  );
  
  // Get cycle info
  const cycles = useDegapProcessStore(state => state.cycles);
  const cycle = useMemo(
    () => loop?.cycleId ? cycles.find(c => c.id === loop.cycleId) : undefined,
    [cycles, loop?.cycleId]
  );
  
  // Get sessions for this loop
  const allSessions = useSessionStore(state => state.sessions);
  const loopSessions = useMemo(() => {
    if (!loop) return [];
    const sessionIds = loop.sessionIds ?? [];
    return allSessions
      .filter(s => sessionIds.includes(s.id))
      .sort((a, b) => {
        const dateA = a.createdAt?.getTime() || 0;
        const dateB = b.createdAt?.getTime() || 0;
        return dateA - dateB;
      });
  }, [allSessions, loop]);
  
  // Get session results for this loop
  const allItemPools = useItemPoolsStore(state => state.itemPools);
  const loopItemPools = useMemo(() => {
    if (!loop) return [];
    const itemPoolId = loop.itemPoolId;
    return itemPoolId ? allItemPools.filter(fl => fl.id === itemPoolId) : [];
  }, [allItemPools, loop]);
  
  // Get items for item details
  const items = useItemsStore(state => state.items);
  const getItemTitle = useCallback((itemId: string) => {
    const item = items.find(c => c.id === itemId);
    return item?.title || 'Unknown Item';
  }, [items]);
  
  // Build session evolution data
  const sessionEvolution: SessionEvolution[] = useMemo(() => {
    return loopSessions.map(session => ({
      sessionId: session.id,
      sessionName: session.name,
      completedAt: session.runCompletedAt,
      itemCount: session.itemIds.length,
      hasOutput: !!session.rankingOutput,
      rankings: session.rankingOutput?.rankedItemIds.map((itemId, idx) => ({
        itemId,
        rank: idx + 1,
        score: session.rankingOutput?.scores[itemId] || 0,
      })) || [],
    }));
  }, [loopSessions]);
  
  // Track how item rankings changed across sessions
  const itemEvolution = useMemo(() => {
    const itemMap = new Map<string, { itemId: string; title: string; ranksBySession: { sessionId: string; rank: number; score: number }[] }>();
    
    sessionEvolution.forEach(se => {
      se.rankings.forEach(r => {
        if (!itemMap.has(r.itemId)) {
          itemMap.set(r.itemId, {
            itemId: r.itemId,
            title: getItemTitle(r.itemId),
            ranksBySession: [],
          });
        }
        itemMap.get(r.itemId)!.ranksBySession.push({
          sessionId: se.sessionId,
          rank: r.rank,
          score: r.score,
        });
      });
    });
    
    return Array.from(itemMap.values());
  }, [sessionEvolution, getItemTitle]);
  
  // Identify high-variance items (ranks changed significantly)
  const highVarianceItems = useMemo(() => {
    return itemEvolution.filter(item => {
      if (item.ranksBySession.length < 2) return false;
      const ranks = item.ranksBySession.map(r => r.rank);
      const maxRank = Math.max(...ranks);
      const minRank = Math.min(...ranks);
      return (maxRank - minRank) >= 3; // Moved 3+ positions
    });
  }, [itemEvolution]);
  
  // Loop not found
  if (!loop) {
    return (
      <div className="px-4 py-12">
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-[var(--coral)] mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-[var(--coral)] mb-2">Loop Not Found</h1>
          <p className="text-[var(--coral)] mb-4">
            The Catchball loop with ID "{loopId}" does not exist or has been deleted.
          </p>
          <Link
            to="/prioritization/reports"
            className="inline-flex items-center gap-2 text-[var(--coral)] hover:text-[var(--coral)]"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Reports
          </Link>
        </div>
      </div>
    );
  }
  
  const ballPossession = getBallPossession(loop.state);
  const progress = getLoopProgress(loop.state);
  const hasSynthesis = !!loop.synthesis;
  
  return (
    <div className="px-6 py-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/prioritization/reports"
          className="inline-flex items-center text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Reports
        </Link>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-pri)]">
              Loop {loop.loopNumber} Roll-Up Report
            </h1>
            <p className="text-[var(--text-sec)] mt-1">
              {cycle?.name || 'Unknown Cycle'} - Catchball Loop Synthesis
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              loop.state === 'output_ready' 
                ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                : loop.state === 'closed'
                ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]'
                : 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
            }`}>
              {LOOP_STATE_LABELS[loop.state]}
            </span>
          </div>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[var(--text-pri)]">Loop Progress</span>
          <span className="text-sm text-[var(--text-ter)]">{progress}%</span>
        </div>
        <div className="h-2 bg-[var(--hover-tint)] rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2 text-xs text-[var(--text-ter)]">
          <span>Ball with: <strong className="text-[var(--text-sec)] capitalize">{ballPossession}</strong></span>
          <span>{loopSessions.length} sessions in this loop</span>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--slate-dim)]">
              <Layers className="h-5 w-5 text-[var(--text-ter)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{loopSessions.length}</p>
              <p className="text-sm text-[var(--text-ter)]">Sessions</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--green-dim)]">
              <Target className="h-5 w-5 text-[var(--green)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{loopItemPools.length}</p>
              <p className="text-sm text-[var(--text-ter)]">Session Results</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--mustard-dim)]">
              <TrendingUp className="h-5 w-5 text-[var(--mustard)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{highVarianceItems.length}</p>
              <p className="text-sm text-[var(--text-ter)]">High Variance Items</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2  ${hasSynthesis ? 'bg-[var(--green-dim)]' : 'bg-[var(--hover-tint)]'}`}>
              <Lightbulb className={`h-5 w-5 ${hasSynthesis ? 'text-[var(--green)]' : 'text-[var(--text-ter)]'}`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{hasSynthesis ? 'Yes' : 'No'}</p>
              <p className="text-sm text-[var(--text-ter)]">Synthesis</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Section 1: Session Lineage */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Session Lineage</h2>
          </div>
          <p className="text-sm text-[var(--text-ter)] mt-1">
            Ordered list of sessions and what each contributed
          </p>
        </div>
        
        {loopSessions.length === 0 ? (
          <div className="p-6 text-center">
            <Info className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-ter)]">No sessions have been created in this loop yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--rule)]">
            {loopSessions.map((session, index) => (
              <SessionLineageCard
                key={session.id}
                session={session}
                index={index}
                totalSessions={loopSessions.length}
              />
            ))}
          </div>
        )}
      </section>
      
      {/* Section 2: Item Evolution */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Item Evolution</h2>
          </div>
          <p className="text-sm text-[var(--text-ter)] mt-1">
            How rankings changed across sessions
          </p>
        </div>
        
        {itemEvolution.length === 0 ? (
          <div className="p-6 text-center">
            <Info className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-ter)]">No ranking data available yet.</p>
          </div>
        ) : (
          <div className="p-6">
            {highVarianceItems.length > 0 && (
              <div className="mb-4 p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                <h3 className="text-sm font-medium text-[var(--mustard)] mb-2">
                  High Variance Items ({highVarianceItems.length})
                </h3>
                <p className="text-xs text-[var(--mustard)] mb-3">
                  These items moved 3 or more positions across sessions, indicating disagreement or evolving understanding.
                </p>
                <ul className="space-y-1">
                  {highVarianceItems.map(item => (
                    <li key={item.itemId} className="text-sm text-[var(--mustard)]">
                      • {item.title}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[var(--rule)]">
                <thead className="bg-[var(--surface)]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">
                      Item
                    </th>
                    {loopSessions.map(session => (
                      <th key={session.id} className="px-4 py-3 text-center text-xs font-medium text-[var(--text-ter)] uppercase">
                        {session.name.slice(0, 15)}...
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--rule)]">
                  {itemEvolution.slice(0, 20).map(item => (
                    <tr key={item.itemId}>
                      <td className="px-4 py-3 text-sm text-[var(--text-pri)] max-w-xs truncate">
                        {item.title}
                      </td>
                      {loopSessions.map(session => {
                        const ranking = item.ranksBySession.find(r => r.sessionId === session.id);
                        return (
                          <td key={session.id} className="px-4 py-3 text-center">
                            {ranking ? (
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium ${
                                ranking.rank <= 3 
                                  ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                                  : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                              }`}>
                                {ranking.rank}
                              </span>
                            ) : (
                              <span className="text-[var(--text-ter)]">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {itemEvolution.length > 20 && (
                <p className="text-xs text-[var(--text-ter)] mt-2 text-center">
                  Showing first 20 of {itemEvolution.length} items
                </p>
              )}
            </div>
          </div>
        )}
      </section>
      
      {/* Section 3: Synthesis Artifacts */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Synthesis</h2>
          </div>
          <p className="text-sm text-[var(--text-ter)] mt-1">
            Middle management synthesis returned to executive
          </p>
        </div>
        
        {!hasSynthesis ? (
          <div className="p-6 text-center">
            <Info className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-ter)]">
              No synthesis has been created yet. The loop must reach the "returned_to_top" state.
            </p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <SynthesisSection synthesis={loop.synthesis!} />
          </div>
        )}
      </section>
      
      {/* Section 4: Session Results Produced */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)]">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <ListOrdered className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              Session Results ({loopItemPools.length})
            </h2>
          </div>
        </div>
        
        {loopItemPools.length === 0 ? (
          <div className="p-6 text-center">
            <Info className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-ter)]">No session results have been produced by this loop yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--rule)]">
            {loopItemPools.map(fl => (
              <div key={fl.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium"><ObjectRef kind="item_pool" id={fl.id} name={fl.name} /></p>
                  <p className="text-sm text-[var(--text-ter)]">
                    {fl.priorityIds?.length || 0} items • {fl.state}
                  </p>
                </div>
                <Link
                  to={`/prioritization/lists/${fl.id}`}
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  View
                  <ExternalLink className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>
      
      {/* Report Footer */}
      <div className="mt-6 text-center text-xs text-[var(--text-ter)]">
        <p>
          Loop Roll-Up Report - Aggregates session results without overriding them.
        </p>
        <p className="mt-1">
          Loop ID: {loop.id}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Session Lineage Card Component
// ============================================================================

interface SessionLineageCardProps {
  session: PrioritizationSession;
  index: number;
  totalSessions: number;
}

function SessionLineageCard({ session, index, totalSessions }: SessionLineageCardProps) {
  const hasOutput = !!session.rankingOutput;
  
  return (
    <div className="p-4">
      <div className="flex items-start gap-4">
        {/* Timeline indicator */}
        <div className="flex flex-col items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            hasOutput ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
          }`}>
            {index + 1}
          </div>
          {index < totalSessions - 1 && (
            <div className="w-0.5 h-8 bg-[var(--hover-tint)] mt-2" />
          )}
        </div>
        
        {/* Session info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="font-medium"><ObjectRef kind="session" name={session.name} /></h3>
            <Link
              to={`/prioritization/reports/session/${session.id}`}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View Report →
            </Link>
          </div>
          
          <div className="flex items-center gap-4 mt-1 text-sm text-[var(--text-ter)]">
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {session.createdAt.toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1">
              <Target className="h-4 w-4" />
              {session.itemIds.length} items
            </span>
            <span className={`flex items-center gap-1 ${hasOutput ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}`}>
              {hasOutput ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Output ready
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4" />
                  Pending
                </>
              )}
            </span>
          </div>
          
          {hasOutput && session.rankingOutput && (
            <p className="text-xs text-[var(--text-ter)] mt-2">
              Produced {session.rankingOutput.rankedItemIds.length} rankings using {session.rankingOutput.toolName}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Synthesis Section Component
// ============================================================================

interface SynthesisSectionProps {
  synthesis: LoopSynthesis;
}

function SynthesisSection({ synthesis }: SynthesisSectionProps) {
  return (
    <>
      {/* Summary */}
      <div>
        <h3 className="text-sm font-medium text-[var(--text-pri)] mb-2">Summary</h3>
        <p className="text-sm text-[var(--text-sec)] bg-[var(--surface)] p-3">
          {synthesis.summary || 'No summary provided.'}
        </p>
      </div>
      
      {/* Tensions */}
      {synthesis.tensions.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-[var(--text-pri)] mb-2">
            Key Tensions ({synthesis.tensions.length})
          </h3>
          <ul className="space-y-2">
            {synthesis.tensions.map((tension, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-[var(--mustard)] bg-[var(--mustard-dim)] p-2 rounded">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {tension}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Recommendations */}
      {synthesis.recommendations.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-[var(--text-pri)] mb-2">
            Recommendations ({synthesis.recommendations.length})
          </h3>
          <ul className="space-y-2">
            {synthesis.recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] p-2 rounded">
                <Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Unresolved Items */}
      {synthesis.unresolvedItems.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-[var(--text-pri)] mb-2">
            Unresolved Items ({synthesis.unresolvedItems.length})
          </h3>
          <ul className="space-y-1">
            {synthesis.unresolvedItems.map((item, idx) => (
              <li key={idx} className="text-sm text-[var(--text-sec)]">
                • {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Provenance */}
      <div className="pt-4 border-t border-[var(--rule)]">
        <p className="text-xs text-[var(--text-ter)]">
          Synthesized by: {synthesis.synthesizedBy} on {synthesis.synthesizedAt?.toLocaleDateString() || 'Unknown date'}
        </p>
      </div>
    </>
  );
}

export default LoopRollUpReport;
